import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import type {
  ListsViewResponse,
  ListDetails,
  ListStat,
  ListUploadResult,
  ListUpdateParams,
} from '@intone/shared/types'

import { useApiClient } from '@/shared/api'
import { convertParamsToString} from '@/shared/lib/queryParams';

interface ListDetailsResponse {
  list: ListDetails
  listStats: ListStat[]
  listLeads: {
    id: string
    phone_number: string | null
    status: string | null
    data: Record<string, unknown>
    list_id: number
    last_call_ts: string | null
  }[]
}

type ListsQueryParams = {
  campaign_id?: number
  page?: number
  pageSize?: number
  sortField?: string
  sortDirection?: 'asc' | 'desc'
}

/**
 * Hook to fetch all lists
 */
export function useLists(params?: ListsQueryParams) {
  const apiClient = useApiClient();

  const queryString = convertParamsToString<ListsQueryParams>(params);

  return useQuery({
    queryKey: ['lists', params],
    queryFn: async () => {
      return apiClient<ListsViewResponse>(`/lists?${queryString}`)
    },
  })
}

/**
 * Hook to fetch single list details
 */
export function useList(listId: number | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['lists', listId],
    queryFn: async () => {
      return apiClient<ListDetailsResponse>(`/lists/${listId}`)
    },
    enabled: listId !== null,
  })
}

/**
 * Hook to update a list
 */
export function useUpdateList() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ listId, data }: { listId: number; data: ListUpdateParams }) => {
      return apiClient<{ message: string }>(`/lists/${listId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}

/**
 * Hook to delete a list
 */
export function useDeleteList() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (listId: number) => {
      return apiClient<{ message: string }>(`/lists/${listId}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'

/**
 * Hook to upload a CSV and create a list
 */
export function useUploadList() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (formData: FormData) => {
      const token = await getToken()
      // Use fetch directly for FormData (can't use apiClient which sets Content-Type: application/json)
      const res = await fetch(`${API_BASE}/lists/upload`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      })

      const data = await res.json()
      if (!data.success) {
        throw new Error(data.error || 'Upload failed')
      }
      return data.data as ListUploadResult
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}